import { ApiPaginationStructure, ApiSuccessResponse } from "./api";

export interface AccommodationImage {
  id: string;
  fileKey: string;
  order: number;
  isCover: boolean;
  altText: string | null;
}

/**
 * Accommodation list item.
 *
 * Shared by `/search/city/{citySlug}` (PLP) and `/discovery/*`. The discovery
 * endpoints return a narrower payload than search, so anything that is not
 * present in both is optional here.
 */
export interface Accommodation {
  accommodationId: string;
  title: string;
  /** number on search, string on discovery */
  referenceId: number | string;
  /** `{citySlug}/{referenceId}` — only returned by search */
  referenceParam?: string;
  /** only returned by discovery */
  hashId?: string;
  typeName?: string;
  typeSlug?: string;
  typeId?: number;
  citySlug?: string;
  cityName?: string;
  cityId?: string;
  provinceName?: string;
  rooms?: number | null;
  price: number;
  discountedPrice: number;
  discount: number;
  /** only returned by discovery */
  discountType?: string | null;
  hasDiscount?: boolean;
  isNew?: boolean;
  baseCapacity: number;
  extraCapacity?: number | null;
  totalCapacity?: number | null;
  isInstantBookable: boolean;
  /** number on search, string ("5.00") on discovery */
  overallRating?: number | string;
  /** only returned by discovery */
  reserveDate?: string;
  images: AccommodationImage[];
}

export type AccommodationResponse = ApiSuccessResponse<Accommodation[]>;

export type AccommodationSearchResponse = ApiPaginationStructure<
  Accommodation[]
>;

export interface VillaCardProps {
  villa: Accommodation;
  cityName?: string;
  stateName?: string;
  title?: string;
  rooms?: number;
  guests?: number;
}

/**
 * Builds the PDP href for a list item. Search results carry `referenceParam`
 * directly; discovery results only carry the pieces, so fall back to them.
 * Optional stay dates are forwarded as query params so the PDP calendar
 * opens on the same check-in / check-out the user picked on the PLP.
 */
export function getAccommodationHref(
  accommodation: Pick<
    Accommodation,
    "referenceParam" | "citySlug" | "referenceId"
  >,
  stay?: {
    checkIn?: string | null;
    checkOut?: string | null;
  },
): string | null {
  let path: string | null = null;

  if (accommodation.referenceParam) {
    path = `/accommodations/${accommodation.referenceParam}`;
  } else if (accommodation.citySlug && accommodation.referenceId) {
    path = `/accommodations/${accommodation.citySlug}/${accommodation.referenceId}`;
  }

  if (!path) return null;

  const params = new URLSearchParams();
  if (stay?.checkIn) params.set("checkIn", stay.checkIn);
  if (stay?.checkOut) params.set("checkOut", stay.checkOut);
  const query = params.toString();

  return query ? `${path}?${query}` : path;
}
